import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styles from './AboutPage.module.scss';

const AboutPage: React.FC = () => {
  const location = useLocation();
  const isPublic = !location.pathname.startsWith('/dashboard');
  const backTo = isPublic ? '/' : '/dashboard';
  const backLabel = isPublic ? 'Back to Home' : 'Back to Dashboard';

  const [whyOpen, setWhyOpen] = useState<number | null>(0);
  const [ragOpen, setRagOpen] = useState<number | null>(null);

  const whyItems = [
    { title: 'What business teams require', body: 'Accuracy, governance, traceability, and security by default.' },
    { title: 'Challenges we consistently hear', body: 'Teams repeatedly answer the same questions, support queues grow, policies are interpreted inconsistently, and sensitive information cannot be exposed to public AI tools.' },
    { title: 'Our approach', body: 'A retrieval-augmented assistant designed for organizations, grounded only in approved internal knowledge.' },
  ];

  const ragMistakes = [
    { title: 'Too much or too little context', body: 'Weak chunking and indexing create incomplete or irrelevant answers. Arihex applies structured retrieval and filtering to return only high-signal context.' },
    { title: 'Cross-tenant data mixing', body: 'Some systems risk exposing one customer\'s information to another. Arihex enforces strict workspace-level isolation.' },
    { title: 'Responses without traceability', body: 'Uncited answers reduce trust. Arihex includes source-level references so responses can be verified quickly.' },
    { title: 'Ignoring structured data', body: 'Many RAG stacks focus only on documents. Arihex supports both live structured systems (PostgreSQL, MySQL) and unstructured files (Excel, CSV, PDF).' },
    { title: 'Missing governance controls', body: 'AI should never surface unauthorized data. Role-based controls ensure users can access only what they are permitted to see.' },
    { title: 'Confident answers without evidence', body: 'When relevant information is unavailable, Arihex indicates that clearly instead of fabricating an answer.' },
  ];

  const differentiators = [
    { title: 'Grounded answers only', desc: 'Responses are generated from connected internal data. If evidence is unavailable, the assistant says so.' },
    { title: 'Citations with every response', desc: 'Each answer links back to the underlying record, row, or document passage.' },
    { title: 'Security by architecture', desc: 'Data is isolated per workspace, encrypted in transit and at rest, and never used to train shared public models.' },
    { title: 'Consistent organizational knowledge', desc: 'Teams receive one verified answer across support, operations, and internal workflows.' },
    { title: 'Operational governance built in', desc: 'Role-based access, auditability, and usage visibility are available from day one.' },
  ];

  const audiencePills = [
    'Customer support and success teams',
    'Operations and process teams',
    'IT, security, and compliance',
    'Mid-market and enterprise SaaS',
  ];

  const resultItems = [
    'Lower average response time',
    'Reduced repetitive requests',
    'Faster onboarding to company knowledge',
    'Higher confidence in internal answers',
    'Better visibility into knowledge gaps',
  ];

  const hostingOptions = [
    { title: 'Managed cloud', desc: 'Launch quickly on our managed multi-tenant cloud with minimal infrastructure overhead.' },
    { title: 'Dedicated private cloud', desc: 'Use isolated runtime for stronger tenant separation and predictable performance.' },
    { title: 'Your cloud (AWS, GCP, Azure)', desc: 'Deploy Arihex in your environment with your networking, security, and governance policies.' },
    { title: 'On-premises', desc: 'Run Arihex in your data center or private cloud for strict residency and air-gapped requirements.' },
  ];

  return (
    <div className={styles.aboutPage}>
      <nav className={styles.nav}>
        <Link to={backTo} className={styles.backLink}>{backLabel}</Link>
      </nav>

      <header className={styles.hero}>
        <p className={styles.heroLabel}>About Arihex</p>
        <h1 className={styles.heroTitle}>Your company knowledge, delivered with clarity and control.</h1>
        <p className={styles.heroSub}>
          Every organization already has the right answers. They are simply distributed across databases, spreadsheets, documents, and internal tools. Arihex transforms that knowledge into a secure AI layer that delivers accurate, verifiable responses grounded in your own data.
        </p>
      </header>

      <section className={styles.section} aria-labelledby="data-sources">
        <p className={styles.sectionLabel} id="data-sources">Platform</p>
        <h2 className={styles.sectionTitle}>Built to work with your data</h2>
        <div className={styles.featureCards}>
          <article className={styles.featureCard}>
            <div className={styles.featureCardHeader}>
              <span className={styles.featureCardIcon} aria-hidden>Database</span>
              <h3>Database sources</h3>
            </div>
            <p>Connect to PostgreSQL and MySQL. Query operational data securely and get answers backed by real database records.</p>
            <ul>
              <li>PostgreSQL</li>
              <li>MySQL</li>
            </ul>
          </article>
          <article className={styles.featureCard}>
            <div className={styles.featureCardHeader}>
              <span className={styles.featureCardIcon} aria-hidden>Files</span>
              <h3>File-based sources</h3>
            </div>
            <p>Upload Excel, CSV, and PDF. Turn static files into searchable, intelligent knowledge.</p>
            <ul>
              <li>Excel</li>
              <li>CSV</li>
              <li>PDF</li>
            </ul>
          </article>
        </div>
        <p className={styles.sectionTrail}>
          Whether your information lives in a database or in documents, we bring it together into one reliable AI layer.
        </p>
      </section>

      <section className={styles.section} aria-labelledby="hosting">
        <p className={styles.sectionLabel} id="hosting">Hosting & deployment</p>
        <h2 className={styles.sectionTitle}>Deploy where you need it</h2>
        <p className={styles.sectionLead}>
          Choose the deployment model that aligns with your infrastructure, governance standards, and compliance requirements.
        </p>
        <div className={styles.featureCards}>
          {hostingOptions.map((opt, i) => (
            <article key={i} className={styles.featureCard}>
              <h3>{opt.title}</h3>
              <p>{opt.desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.section} aria-labelledby="why">
        <p className={styles.sectionLabel} id="why">Mission</p>
        <h2 className={styles.sectionTitle}>Why we built this</h2>
        <p className={styles.sectionLead}>
          General-purpose AI is useful, but enterprise teams require reliability, explainability, and control.
        </p>
        <div className={styles.accordionList}>
          {whyItems.map((item, i) => (
            <div
              key={i}
              className={`${styles.accordionItem} ${whyOpen === i ? styles.open : ''}`}
            >
              <button
                type="button"
                className={styles.accordionHeader}
                onClick={() => setWhyOpen(whyOpen === i ? null : i)}
                aria-expanded={whyOpen === i}
              >
                <span>{item.title}</span>
                <span className={styles.accordionChevron} aria-hidden />
              </button>
              <div className={styles.accordionBody}>
                <div className={styles.accordionContent}>{item.body}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.section} aria-labelledby="differentiators">
        <p className={styles.sectionLabel} id="differentiators">Differentiators</p>
        <h2 className={styles.sectionTitle}>What makes us different</h2>
        <p className={styles.sectionLeadItalic}>Built for real business use — not experiments.</p>
        <ul className={styles.diffGrid}>
          {differentiators.map((d, i) => (
            <li key={i} className={styles.diffCard}>
              <h3>{d.title}</h3>
              <p>{d.desc}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className={styles.section} aria-labelledby="rag">
        <p className={styles.sectionLabel} id="rag">Engineering</p>
        <h2 className={styles.sectionTitle}>Common RAG mistakes we avoid</h2>
        <p className={styles.sectionLead}>
          Most production failures come from retrieval design and governance gaps, not model quality alone.
        </p>
        <ul className={styles.ragList}>
          {ragMistakes.map((item, i) => (
            <li key={i} className={`${styles.ragItem} ${ragOpen === i ? styles.open : ''}`}>
              <button
                type="button"
                className={styles.ragHeader}
                onClick={() => setRagOpen(ragOpen === i ? null : i)}
                aria-expanded={ragOpen === i}
              >
                <span className={styles.ragNumber}>{i + 1}</span>
                <span className={styles.ragTitle}>{item.title}</span>
                <span className={styles.ragChevron} aria-hidden />
              </button>
              <div className={styles.ragBody}>
                <div className={styles.ragContent}>{item.body}</div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className={styles.section} aria-labelledby="audience">
        <p className={styles.sectionLabel} id="audience">Audience</p>
        <h2 className={styles.sectionTitle}>Who it's for</h2>
        <p className={styles.audienceIntro}>Teams that depend on accurate and timely information:</p>
        <ul className={styles.audiencePills}>
          {audiencePills.map((label, i) => (
            <li key={i}><span className={styles.audiencePill}>{label}</span></li>
          ))}
        </ul>
        <p className={styles.audienceTrail}>Anywhere data needs to be fast, reliable, and secure.</p>
      </section>

      <section className={styles.section} aria-labelledby="results">
        <p className={styles.sectionLabel} id="results">Outcomes</p>
        <h2 className={styles.sectionTitle}>The result</h2>
        <ul className={styles.resultList}>
          {resultItems.map((label, i) => (
            <li key={i} className={styles.resultItem}>
              <span className={styles.resultCheck} aria-hidden />
              {label}
            </li>
          ))}
        </ul>
        <p className={styles.resultTrail}>
          Improvement comes from trusted retrieval and governance, not from unchecked model confidence.
        </p>
      </section>

      <section className={styles.beliefBlock} aria-labelledby="belief">
        <p className={styles.sectionLabel} id="belief">Vision</p>
        <h2 className={styles.sectionTitle}>Our belief</h2>
        <p>
          The future of work is not about more AI. It is about the right AI, connected to the right data. Organizational knowledge should be accessible, accurate by default, secure, and auditable. That is what we are building.
        </p>
      </section>

      <section className={styles.ctaSection} aria-labelledby="cta">
        <h2 id="cta" className={styles.ctaTitle}>Ready to see it in action?</h2>
        <p className={styles.ctaSteps}>
          Create a workspace, connect your data sources, and ask your first question in minutes.
        </p>
        <Link to={backTo} className={styles.ctaButton}>
          {isPublic ? 'Go to Home' : 'Go to Dashboard'}
        </Link>
      </section>
    </div>
  );
};

export default AboutPage;
